import { useContext, useEffect, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useMatch, useNavigate } from 'react-router';
import { Users } from 'lucide-react';
import { hasPermission } from '@helios/authz';
import { cn } from '@helios/ui';
import { AuthContext } from '@/features/auth/authContext';
import type { SubjectHub } from '@/navigation/hubs';
import { DensePagePanel, DensePageShell } from './DensePageShell';
import { ProcessPhaseRail } from './ProcessPhaseRail';
import {
  filterHubTabsForPermissions,
  findActiveHubPhase,
  isAccessibleDestinationPath,
  findActiveHubTab,
  pathMatchesTab,
  resolveHubPhases,
  resolveHubTabs,
  type ResolvedHubTab,
} from './subjectHub';

export function SubjectHubPage({ hub, children }: { hub: SubjectHub; children: ReactNode }) {
  const auth = useContext(AuthContext);
  const permissions = auth ? auth.permissions : null;
  const location = useLocation();
  const navigate = useNavigate();
  const crmReader =
    hub.id === 'unified-crm' &&
    permissions !== null &&
    hasPermission(permissions, 'crm:unified:read');
  const tabs = filterHubTabsForPermissions(resolveHubTabs(hub), permissions)
    .filter((tab) => !crmReader || !['/crm/clients', '/crm/vendors'].includes(tab.to))
    .map((tab) =>
      crmReader && tab.to === '/crm'
        ? { ...tab, activePaths: [...(tab.activePaths ?? []), '/crm/clients', '/crm/vendors'] }
        : tab,
    );
  const phases = resolveHubPhases(hub.phases, tabs);
  const active = findActiveHubTab(location.pathname, tabs);
  const activePhase = findActiveHubPhase(active, phases);

  useEffect(() => {
    if (tabs.length === 0) return;
    if (active) return;
    // A screen the user may open stays put even when its owning tab is hidden.
    if (permissions && isAccessibleDestinationPath(location.pathname, permissions)) return;
    const first = tabs[0];
    if (first && location.pathname !== first.to) {
      navigate(first.to, { replace: true });
    }
  }, [active, location.pathname, navigate, permissions, tabs]);

  return (
    <DensePageShell>
      <div className="hx-section-header border-border/50 backdrop-blur-glass flex shrink-0 flex-col border-b px-2 pt-1">
        {phases.length > 0 ? (
          <div className="flex min-w-0 items-end gap-2">
            <HubTitle hub={hub} />
            <ProcessPhaseRail
              phases={phases}
              activePhaseId={activePhase?.id}
              ariaLabel={`${hub.label} process`}
            />
            <HubLinks hub={hub} permissions={permissions} />
          </div>
        ) : (
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end">
            <HubTitle hub={hub} />
            <HubTabList hub={hub} tabs={tabs} />
            <HubLinks hub={hub} permissions={permissions} />
          </div>
        )}
        {phases.length > 0 ? <HubTabList hub={hub} tabs={tabs} /> : null}
      </div>
      <DensePagePanel className="min-w-0">{children}</DensePagePanel>
    </DensePageShell>
  );
}

function HubLinks({
  hub,
  permissions,
}: {
  hub: SubjectHub;
  permissions: readonly string[] | null;
}) {
  const links = (hub.links ?? []).filter(
    (link) => permissions !== null && hasPermission(permissions, link.permission),
  );
  if (links.length === 0) return null;
  return (
    <div className="flex shrink-0 items-center gap-1 pb-1">
      {links.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className="border-border text-muted-foreground hover:text-foreground hover:bg-secondary/50 inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-semibold"
        >
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {link.label}
        </Link>
      ))}
    </div>
  );
}

function HubTitle({ hub }: { hub: SubjectHub }) {
  return (
    <div className="min-w-0 shrink-0 pb-1.5 sm:max-w-[14rem] sm:pr-3">
      <p className="hx-eyebrow text-2xs font-semibold uppercase tracking-widest">{hub.eyebrow}</p>
      <h1 className="hx-section-title text-sm font-semibold">{hub.label}</h1>
    </div>
  );
}

function HubTabList({ hub, tabs }: { hub: SubjectHub; tabs: readonly ResolvedHubTab[] }) {
  return (
    <div
      role="tablist"
      aria-label={`${hub.label} views`}
      className="flex min-w-0 flex-1 gap-1 overflow-x-auto pb-px"
      onKeyDown={(event) => {
        const controls = Array.from(
          event.currentTarget.querySelectorAll<HTMLAnchorElement>('[role="tab"]'),
        );
        const index = controls.indexOf(event.target as HTMLAnchorElement);
        if (index < 0) return;
        const next =
          event.key === 'ArrowRight'
            ? (index + 1) % controls.length
            : event.key === 'ArrowLeft'
              ? (index - 1 + controls.length) % controls.length
              : event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? controls.length - 1
                  : null;
        if (next === null) return;
        event.preventDefault();
        controls[next]?.focus();
        controls[next]?.click();
      }}
    >
      {tabs.map((tab) => (
        <SubjectHubTabLink key={tab.to} tab={tab} />
      ))}
    </div>
  );
}

function SubjectHubTabLink({ tab }: { tab: ResolvedHubTab }) {
  const location = useLocation();
  const match = useMatch({ path: tab.to, end: tab.end });
  const selected = Boolean(match) || pathMatchesTab(location.pathname, tab);
  const Icon = tab.icon;

  return (
    <NavLink
      to={tab.to}
      end={tab.end}
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      className={cn(
        'hx-section-tab relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-t-md px-3 py-1.5 text-xs font-semibold tracking-wide transition-colors',
        'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2',
        selected
          ? 'bg-card/80 text-brand-gold after:bg-brand-gold after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full'
          : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {tab.label}
    </NavLink>
  );
}
